import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { X, CheckCircle, Download, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function LeadMagnetModal({ open, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (!consent) {
      setError("Please confirm your consent to receive the guide and advisory updates.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        name: name.trim(),
        email: email.trim(),
        resource: "career-roadmap",
        consent: true,
      });
      setSuccess(true);
    } catch {
      setError("Something went wrong. Please try again.");
    }
    setLoading(false);
  }

  function handleClose() {
    onClose();
    setTimeout(() => { setSuccess(false); setName(""); setEmail(""); setConsent(false); setError(""); }, 300);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-ivory border-soft-gray max-w-md p-0 overflow-hidden">
        <DialogTitle className="sr-only">Download Free Career Roadmap</DialogTitle>

        {/* Navy header */}
        <div className="bg-[#001F3F] px-8 pt-8 pb-7 relative">
          <button onClick={handleClose} className="absolute top-4 right-4 text-white/30 hover:text-white/70 transition-colors" aria-label="Close">
            <X size={16} />
          </button>
          <p className="font-lora text-[10px] tracking-[0.28em] uppercase text-[#C2B8A3] mb-3">Free Resource</p>
          <h2 className="font-lora text-[1.55rem] text-white font-normal leading-snug">
            Free Career Roadmap
            <br /><span className="text-white/60 text-lg">for Young Professionals</span>
          </h2>
          <div className="w-8 h-px bg-[#C2B8A3] mt-5"></div>
        </div>

        <div className="px-8 py-7">
          {success ? (
            <div className="text-center py-2" data-testid="lead-magnet-success">
              <CheckCircle size={30} className="text-[#C2B8A3] mx-auto mb-4" />
              <h3 className="font-lora text-xl text-[#001F3F] mb-2">Your guide is on the way.</h3>
              <p className="text-[#001F3F]/60 font-serif text-sm leading-relaxed mb-6">
                Check your inbox — the Career Roadmap PDF will arrive shortly.
                If you do not see it, check your spam folder.
              </p>
              <button onClick={handleClose} className="text-[#001F3F]/40 font-serif text-xs underline underline-offset-2 hover:text-[#001F3F]/70 transition-colors">
                Close
              </button>
            </div>
          ) : (
            <>
              <p className="text-[#001F3F]/60 font-serif text-sm leading-relaxed mb-6">
                A clear five-step framework to assess where you stand, define your direction,
                and move forward with confidence. Delivered to your inbox as a PDF.
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block font-lora text-[10px] tracking-[0.2em] uppercase text-[#001F3F]/40 mb-1.5">Your Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    required
                    className="w-full border border-[#E0E0E0] bg-white px-3 py-2.5 text-[#001F3F] font-serif text-sm focus:outline-none focus:border-[#001F3F] transition-colors"
                    placeholder="First name"
                    data-testid="lead-magnet-name"
                  />
                </div>
                <div>
                  <label className="block font-lora text-[10px] tracking-[0.2em] uppercase text-[#001F3F]/40 mb-1.5">Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    className="w-full border border-[#E0E0E0] bg-white px-3 py-2.5 text-[#001F3F] font-serif text-sm focus:outline-none focus:border-[#001F3F] transition-colors"
                    placeholder="you@email.com"
                    data-testid="lead-magnet-email"
                  />
                </div>

                {/* CASL consent checkbox */}
                <label className="flex items-start gap-3 cursor-pointer group" data-testid="consent-label">
                  <div className="relative flex-shrink-0 mt-0.5">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={e => setConsent(e.target.checked)}
                      className="sr-only"
                      data-testid="consent-checkbox"
                    />
                    <div
                      className={`w-4 h-4 border transition-all ${
                        consent
                          ? "bg-[#001F3F] border-[#001F3F]"
                          : "bg-white border-[#C8C5BF] group-hover:border-[#001F3F]/40"
                      }`}
                    >
                      {consent && (
                        <svg viewBox="0 0 12 10" fill="none" className="w-full h-full p-0.5">
                          <path d="M1 5l3.5 3.5L11 1" stroke="#C2B8A3" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span className="text-[#001F3F]/55 font-serif text-xs leading-relaxed">
                    I agree to receive the Career Roadmap and occasional advisory updates from{" "}
                    <span className="text-[#001F3F]/75">De Nicola Consulting Co.</span>
                    {" "}I can unsubscribe at any time.
                  </span>
                </label>

                {error && <p className="text-red-500 font-serif text-xs" data-testid="lead-magnet-error">{error}</p>}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 bg-[#001F3F] text-white py-3 text-xs tracking-[0.18em] uppercase hover:bg-[#001F3F]/90 transition-all disabled:opacity-60"
                  data-testid="lead-magnet-submit"
                >
                  {loading ? <><Loader2 size={13} className="animate-spin" /> Sending…</> : <><Download size={13} /> Send My Free Roadmap</>}
                </button>
              </form>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
